"use client";

import { useCallback, useEffect, useRef } from "react";
import { EditionJson } from "@/lib/api";
import type { PhotoEngagement } from "@/lib/api/photoEngagement";
import { createPhotoViewQueue } from "@/lib/photoViewQueue";
import { observePhotoImpressions } from "@/lib/observePhotoImpressions";

// Share queues across galleries, clones and modal remounts for this document.
const queues = new Map<string, ReturnType<typeof createPhotoViewQueue>>();
function record(photo: PhotoEngagement | undefined, kind: "open" | "impression") {
  if (!photo?.workPhotoId || !photo.salonId) return;
  if (window.location.pathname.startsWith("/dev")) return;
  let queue = queues.get(photo.salonId);
  if (!queue) {
    queue = createPhotoViewQueue(photo.salonId, (body) => EditionJson.recordPhotoViews(body));
    queues.set(photo.salonId, queue);
  }
  if (kind === "open") queue.trackOpen(photo.workPhotoId);
  else queue.trackImpression(photo.workPhotoId);
}
const flush = () => queues.forEach((queue) => queue.flush());

export function usePhotoEngagement(enabled = true) {
  const root = useRef<HTMLDivElement>(null);
  const trackOpen = useCallback((photo: PhotoEngagement | undefined) => {
    if (enabled) record(photo, "open");
  }, [enabled]);
  useEffect(() => {
    if (!enabled || !root.current || window.location.pathname.startsWith("/dev")) return;
    const stop = observePhotoImpressions(root.current, (photo) => record(photo, "impression"));
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [enabled]);
  return { root, trackOpen };
}
